import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Nav from "@/components/welp/Nav";
import PageBg from "@/components/welp/PageBg";
import { useAuth } from "@/contexts/AuthContext";
import { BookOpen, MessageSquare } from "lucide-react";

const STORY_MAX = 2500;
const REFLECTION_MAX = 500;

export default function MyStoryPage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [story, setStory] = useState("");
  const [reflection, setReflection] = useState("");
  const [attachedProfile, setAttachedProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/my-story", { replace: true }); return; }
    api.getMyStory().then(d => {
      if (d.story) {
        // Privacy by design: never re-display a submitted story in the form.
        setSaved(true);
        setAttachedProfile(d.story.attached_profile_id || null);
      }
    }).catch(() => {});
  }, [user, loading, nav]);

  if (loading) return <PageBg><Nav /><div className="p-20 text-center text-gray-300 font-semibold">Checking your session…</div></PageBg>;
  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (story.trim().length < 20) { toast.error("Your story needs at least 20 characters."); return; }
    if (reflection.trim().length < 5) { toast.error("Please answer the reflection question."); return; }
    setBusy(true);
    try {
      const r = await api.saveMyStory(story, reflection);
      setAttachedProfile(r.attached_profile_id || null);
      setSaved(true);
      setStory(""); setReflection("");
      toast.success("Your story is saved. The box clears for your privacy.");
    } catch (err) {
      const det = err?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.message || "Couldn't save your story."));
    } finally { setBusy(false); }
  };

  return (
    <PageBg>
      <Nav />
      <section className="max-w-3xl mx-auto px-6 py-14" data-testid="my-story-page">
        <div className="text-veil">
          <div className="text-sm uppercase tracking-[0.32em] font-bold mb-3"><span className="text-[#FF1A82]">Welp</span> <span className="text-[#5A5A5A]">·</span> Tell My Story</div>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-[#1A1A1A]">Your dating <span className="italic text-[#FF1A82]">story.</span></h1>
          <p className="mt-4 text-[#1A1A1A] text-lg font-semibold">Share what you've been through. Up to {STORY_MAX} characters. AI moderated — no personal info, no harassment, no targeting.</p>
          {attachedProfile && <p className="mt-2 text-lg text-[#FF1A82] font-bold">✓ Your story will appear on your claimed profile.</p>}
          {!attachedProfile && <p className="mt-2 text-lg text-[#5A5A5A] font-semibold">Tip: claim a profile to attach this story there.</p>}
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5 rounded-3xl bg-white/70 backdrop-blur-lg shadow-lg border border-black/10 p-7">
          <div>
            <label className="text-lg uppercase tracking-[0.12em] text-[#FF1A82] font-bold">Your story</label>
            <textarea data-testid="story-text" required minLength={20} maxLength={STORY_MAX} rows={10}
              value={story} onChange={e => setStory(e.target.value)} placeholder="Share what shaped you in dating…"
              className="mt-2 w-full bg-white/60 border border-black/15 rounded-xl px-4 py-3 text-lg text-[#1A1A1A] font-semibold outline-none focus:border-[#FF1A82] resize-none" />
            <div className="text-lg text-[#5A5A5A] text-right font-semibold">{story.length}/{STORY_MAX}</div>
          </div>

          <div>
            <label className="text-lg uppercase tracking-[0.12em] text-[#10B981] font-bold">If you could change anything, what would it be?</label>
            <textarea data-testid="reflection-text" required minLength={5} maxLength={REFLECTION_MAX} rows={4}
              value={reflection} onChange={e => setReflection(e.target.value)} placeholder="Your honest reflection…"
              className="mt-2 w-full bg-white/60 border border-black/15 rounded-xl px-4 py-3 text-lg text-[#1A1A1A] font-semibold outline-none focus:border-[#10B981] resize-none" />
            <div className="text-lg text-[#5A5A5A] text-right font-semibold">{reflection.length}/{REFLECTION_MAX}</div>
          </div>

          <button disabled={busy} data-testid="story-submit"
            className="w-full px-5 py-3 rounded-full bg-[#FF1A82] text-white font-bold text-lg hover:brightness-110 disabled:opacity-60 inline-flex items-center justify-center gap-2">
            <BookOpen className="w-5 h-5" /> {busy ? "Saving…" : (saved ? "Update my story" : "Save my story")}
          </button>
        </form>

        {attachedProfile && (
          <div className="mt-6 rounded-2xl bg-white/70 backdrop-blur-lg border-2 border-[#10B981] p-5">
            <h3 className="font-heading text-xl font-bold text-[#1A1A1A] flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#10B981]" /> Your story is live</h3>
            <p className="text-lg text-[#1A1A1A] font-semibold mt-1">It appears on your claimed profile for the community to read.</p>
            <Link to={`/profile/${attachedProfile}`} className="mt-3 inline-flex px-4 py-2 rounded-full bg-[#10B981] text-white font-bold text-lg">Open my profile</Link>
          </div>
        )}
      </section>
    </PageBg>
  );
}
